import Dexie, { type EntityTable } from 'dexie'
import type { AppNotification, Bookmark, ClinicalCase, CommunityPost, CommunityReply, DownloadRecord, Flashcard, Highlight, Lesson, MedicalLibraryArticle, Note, Progress, Question, QuestionBank, QuizAttempt, QuizSession, SessionAnswer, SessionSnapshot, Subject, User } from '../types'
import { IMED_MEDICAL_LIBRARY_ARTICLES } from '../data/imed-medical-library'

export interface SettingRecord { key: string; value: unknown; updatedAt: string }
export interface MediaBlobRecord { resourceId: string; blob: Blob; mimeType: string; sizeBytes: number; createdAt: string }
export interface SyncTombstone { id: string; userId: string; table: 'highlights' | 'bookmarks'; createdAt: string }
export interface QbankSyncTombstone { id: string; userId: string; collection: 'attempts' | 'bookmarks' | 'sessions' | 'snapshots' | 'answers'; createdAt: string }

class IMedDatabase extends Dexie {
  users!: EntityTable<User, 'id'>
  subjects!: EntityTable<Subject, 'id'>
  lessons!: EntityTable<Lesson, 'id'>
  libraryArticles!: EntityTable<MedicalLibraryArticle, 'id'>
  questions!: EntityTable<Question, 'id'>
  questionBanks!: EntityTable<QuestionBank, 'id'>
  attempts!: EntityTable<QuizAttempt, 'id'>
  quizSessions!: EntityTable<QuizSession, 'id'>
  progress!: EntityTable<Progress, 'id'>
  notes!: EntityTable<Note, 'id'>
  highlights!: EntityTable<Highlight, 'id'>
  bookmarks!: EntityTable<Bookmark, 'id'>
  flashcards!: EntityTable<Flashcard, 'id'>
  notifications!: EntityTable<AppNotification, 'id'>
  community!: EntityTable<CommunityPost, 'id'>
  communityReplies!: EntityTable<CommunityReply, 'id'>
  clinicalCases!: EntityTable<ClinicalCase, 'id'>
  downloads!: EntityTable<DownloadRecord, 'id'>
  mediaBlobs!: EntityTable<MediaBlobRecord, 'resourceId'>
  syncTombstones!: EntityTable<SyncTombstone, 'id'>
  qbankSyncTombstones!: EntityTable<QbankSyncTombstone, 'id'>
  settings!: EntityTable<SettingRecord, 'key'>
  sessionSnapshots!: EntityTable<SessionSnapshot, 'sessionId'>
  sessionAnswers!: EntityTable<SessionAnswer, 'sessionId'>

  constructor() {
    super('iMedDatabase')
    this.version(1).stores({
      users: 'id, &email, &username, role, createdAt',
      subjects: 'id, name',
      lessons: 'id, subjectId, type, order, *tags',
      questions: 'id, subjectId, difficulty, *tags',
      attempts: 'id, userId, questionId, correct, createdAt, [userId+questionId]',
      progress: 'id, userId, entityId, entityType, updatedAt, [userId+entityId]',
      notes: 'id, userId, contextType, contextId, parentId, updatedAt, deletedAt, *tags',
      highlights: 'id, userId, lessonId, sectionId, updatedAt, [userId+lessonId]',
      bookmarks: 'id, userId, entityId, entityType, createdAt, [userId+entityId]',
      flashcards: 'id, userId, subjectId, dueAt, [userId+dueAt]',
      notifications: 'id, userId, read, createdAt, [userId+read]',
      community: 'id, userId, topic, createdAt',
      clinicalCases: 'id, specialty, difficulty',
      downloads: 'id, userId, resourceId, status, updatedAt, [userId+resourceId]',
      settings: 'key, updatedAt',
    })
    this.version(2).stores({ mediaBlobs: 'resourceId, createdAt' })
    this.version(3).stores({ syncTombstones: 'id, userId, table, createdAt' })
    this.version(4).stores({ communityReplies: 'id, postId, userId, createdAt' })
    this.version(5).stores({
      questionBanks: 'id, name, sourceType, installed, createdAt',
      questions: 'id, bankId, subjectId, category, subcategory, difficulty, *tags',
    })
    this.version(6).stores({
      quizSessions: 'id, userId, bankId, packageId, status, createdAt, [userId+bankId], [userId+status]',
    })
    this.version(7).stores({
      sessionSnapshots: 'sessionId, updatedAt',
      sessionAnswers: '[sessionId+questionId], sessionId, questionId, submittedAt',
    })
    this.version(8).stores({
      libraryArticles: 'id, collectionId, systemId, system, title, order, *tags, updatedAt',
    })
    this.version(9).stores({
      qbankSyncTombstones: 'id, userId, collection, createdAt',
    })
  }
}

export const db = new IMedDatabase()

export async function initializeDatabase() {
  const cleanupKey = 'migration:remove-demo-content:v1'
  if (!await db.settings.get(cleanupKey)) {
    await db.transaction('rw', [db.subjects, db.lessons, db.questions, db.questionBanks, db.clinicalCases, db.settings], async () => {
      await db.subjects.clear()
      await db.lessons.clear()
      await db.clinicalCases.clear()
      await db.questions.where('bankId').equals('imed-core').delete()
      await db.questions.where('id').anyOf(Array.from({ length: 12 }, (_, index) => `q${index + 1}`)).delete()
      await db.questionBanks.delete('imed-core')
      await db.settings.put({ key: cleanupKey, value: true, updatedAt: new Date().toISOString() })
    })
  }

  const librarySeedKey = 'seed:imed-medical-library:v1'
  if (!await db.settings.get(librarySeedKey)) {
    await db.transaction('rw', [db.libraryArticles, db.settings], async () => {
      await db.libraryArticles.bulkPut(IMED_MEDICAL_LIBRARY_ARTICLES)
      await db.settings.put({ key: librarySeedKey, value: true, updatedAt: new Date().toISOString() })
    })
  }
}

export const makeId = (prefix: string) => `${prefix}-${crypto.randomUUID()}`
